// Warehouse / Coletare module API helpers.
import { api, backendOrigin, getToken } from "@/lib/api";

export async function listTasks({ status = null, limit = 100, skip = 0 } = {}) {
  const { data } = await api.get("/warehouse/tasks", {
    params: { status: status || undefined, limit, skip },
  });
  return data;
}

export async function createTask(body) {
  const { data } = await api.post("/warehouse/tasks", body);
  return data;
}

export async function updateTask(taskId, body) {
  const { data } = await api.patch(`/warehouse/tasks/${encodeURIComponent(taskId)}`, body);
  return data;
}

export async function deleteTask(taskId) {
  await api.delete(`/warehouse/tasks/${encodeURIComponent(taskId)}`);
}

export async function searchProducts(q = "", limit = 20) {
  const { data } = await api.get("/warehouse/products/search", { params: { q, limit } });
  return data;
}

export async function getProductOverride(productId) {
  const { data } = await api.get(`/warehouse/products/${encodeURIComponent(productId)}/override`);
  return data;
}

export async function setProductOverride(productId, body) {
  const { data } = await api.put(`/warehouse/products/${encodeURIComponent(productId)}/override`, body);
  return data;
}

export async function uploadWarehouseImage(productId, file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(
    `/warehouse/products/${encodeURIComponent(productId)}/image`,
    fd,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

export async function deleteWarehouseImage(productId) {
  const { data } = await api.delete(`/warehouse/products/${encodeURIComponent(productId)}/image`);
  return data;
}

export async function listWarehouseImages({ q = "", only_missing = false, limit = 100, skip = 0 } = {}) {
  const { data } = await api.get("/warehouse/warehouse-images", {
    params: { q: q || undefined, only_missing: only_missing || undefined, limit, skip },
  });
  return data;
}

export async function uploadTaskImage(taskId, file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(
    `/warehouse/tasks/${encodeURIComponent(taskId)}/image`,
    fd,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

export async function getTvConfig() {
  const { data } = await api.get("/warehouse/tv/config");
  return data;
}

export async function rotateTvToken() {
  const { data } = await api.post("/warehouse/tv/rotate");
  return data;
}

export async function getAnalytics() {
  const { data } = await api.get("/warehouse/analytics");
  return data;
}

/* --- TV terminal (no JWT) --- */
export async function tvListTasks(token, limit = 20) {
  const r = await fetch(
    `${backendOrigin()}/api/warehouse/tasks/tv?token=${encodeURIComponent(token)}&limit=${limit}`
  );
  if (!r.ok) throw new Error(`tv list failed: ${r.status}`);
  return await r.json();
}

export async function tvStartTask(token, taskId) {
  const r = await fetch(
    `${backendOrigin()}/api/warehouse/tasks/${encodeURIComponent(taskId)}/start?token=${encodeURIComponent(token)}`,
    { method: "POST" }
  );
  if (!r.ok) throw new Error(`tv start failed: ${r.status}`);
  return await r.json();
}

export async function tvDoneTask(token, taskId) {
  const r = await fetch(
    `${backendOrigin()}/api/warehouse/tasks/${encodeURIComponent(taskId)}/done?token=${encodeURIComponent(token)}`,
    { method: "POST" }
  );
  if (!r.ok) throw new Error(`tv done failed: ${r.status}`);
  return await r.json();
}

export function tvStreamUrl(token) {
  return `${backendOrigin()}/api/warehouse/tv/stream?token=${encodeURIComponent(token)}`;
}

export function tvImageUrl(imageUrl, token) {
  // Backend returns "/api/warehouse/images/<path>" — append token for TV mode.
  if (!imageUrl) return null;
  if (imageUrl.startsWith("http")) return imageUrl;
  return `${backendOrigin()}${imageUrl}${imageUrl.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
}

/* Authenticated image URL builder (manager dashboard).
 * The /api/warehouse/images/{path} endpoint accepts both TV-token requests and
 * un-tokenized requests (when the path exists in DB — sufficient for internal use).
 */
export function managerImageUrl(imageUrl) {
  if (!imageUrl) return null;
  if (imageUrl.startsWith("http")) return imageUrl;
  return `${backendOrigin()}${imageUrl}`;
}
